import type { RecursiveReadonly } from "@p0security/util/types/util";

/** Called by a function to notify its listener(s) that an error has occurred */
export type ErrorHandler = (error: any) => void;

export type Shutdownable = { shutdown?: () => Promise<void> };

/** Represents an access-request policy configuration in Firestore */
export type PolicyApprovalSetting = {
  /** If true, allows approvers to self-approve requests */
  allowOneParty?: boolean;
  requireReason?: boolean;
};

export type Actor = {
  sub: string;
  iss: string;
};

/** A P0 Human user or headless agent */
export type P0User = RecursiveReadonly<{
  /** Currently always assumed to equal email; typically the OIDC subject claim.
   *
   * When 'email' claim does not equal 'uid' (e.g. Okta identities), use email instead.
   */
  uid: string;
  /** OIDC email claim; presence implies email_verified is true */
  email: string;
  /** OIDC issuer claim */
  iss?: string;
  /** Agentic gateway session ID */
  sid?: string;
  /** Installed agentic gateway component ID that minted this user's token */
  gatewayId?: string;
  /** Installed agentic identity-provider component ID that approved this user,
   * if the user is a federated agent
   */
  identityProviderId?: string;
  /** Agents acting on behalf of the user
   *
   * With an AI policy, the first actor is a MCP session identity, remaining actors are AI agents.
   */
  actors?: Actor[];
  provider?: string;
  groups?: string[];
  profile?: {
    firstName?: string;
    lastName?: string;
    displayName?: string;
    [key: string]: unknown;
  };
}>;

export type SlackAuthentication = {
  type: "slack";
};

export type TeamsAuthentication = {
  type: "teams";
};

export type TokenAuthentication = {
  type: "token";
  tokenId: string;
};

export type AuthTrail =
  | SlackAuthentication
  | TeamsAuthentication
  | TokenAuthentication;

export type AuthenticatedP0User = P0User & {
  readonly authTrail: Readonly<AuthTrail>;
};

export type ApiUser = {
  isApiUser: true;
  uid: string;
  name: string;
};

export const isApiUser = (
  user: ApiUser | P0User | undefined
): user is ApiUser => !!user && "isApiUser" in user && user.isApiUser;

export const assertNeverError = (value: never) =>
  Object.assign(new Error("Got unexpected value of type never"), {
    value,
  });

export const assertNever = (value: never) => {
  throw assertNeverError(value);
};
